import { Button, Input, Modal } from '@sjuni/ui';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormError } from '@/components/FormError';
import { SubmitButton } from '@/components/SubmitButton';
import { TranslatableField } from '@/components/form/TranslatableField';
import { type Column, ResourceTable } from '@/components/list/ResourceTable';
import { ResourceToolbar } from '@/components/list/ResourceToolbar';
import { useResourceAction, useResourceList, useSaveResource } from '@/lib/api/resource';
import { useAuth } from '@/lib/auth/useAuth';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import { applyApiErrors } from '@/lib/forms/applyApiErrors';
import type { TranslatedValue } from '@/lib/forms/translatable';
import { valueFor } from '@/lib/forms/translatable';
import { useListParams } from '@/lib/list/useListParams';

import type { NewsCategoryInput, NewsCategoryRow } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §2 — `/news-categories`.
 *
 * A modal rather than a page per record: a category is four fields, and §3 gives
 * `news.manage-taxonomy` to two roles who will open this screen a handful of
 * times a year.
 *
 * The article count is the column that matters. `news.category_id` is
 * `NOT NULL` and `restrictOnDelete()`, so a category with articles behind it
 * cannot be removed at all — the server answers 409 and the count is what makes
 * that answer actionable rather than mysterious.
 */
export function NewsCategoriesPage() {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();
  const { can } = useAuth();
  const list = useListParams('sort_order');

  const [editing, setEditing] = useState<NewsCategoryRow | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<NewsCategoryRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data, isPending, isError } = useResourceList<NewsCategoryRow>(
    'news-categories',
    list.params,
  );

  const act = useResourceAction<NewsCategoryRow>('news-categories');

  const mayManage = can('news.manage-taxonomy');

  const columns: Column<NewsCategoryRow>[] = [
    {
      key: 'name',
      label: t('news.categories.columns.name'),
      render: (row) => (
        <span className="inline-flex items-center gap-2 font-semibold text-primary">
          {row.color ? (
            // The swatch the public filter bar renders. Inline because the
            // colour is data — there is no Tailwind class for "whatever the
            // editor typed".
            <span
              aria-hidden="true"
              style={{ backgroundColor: row.color }}
              className="inline-block h-3 w-3 rounded-full border border-subtle"
            />
          ) : null}
          {valueFor(row.name, locale)}
        </span>
      ),
    },
    {
      key: 'slug',
      label: t('news.categories.columns.slug'),
      render: (row) => (
        <code className="text-caption text-secondary">{valueFor(row.slug, locale)}</code>
      ),
    },
    {
      key: 'translations',
      label: t('news.columns.translations'),
      render: (row) => (
        <span className="text-caption uppercase text-secondary">
          {row.translations_available.join(', ') || '—'}
        </span>
      ),
    },
    {
      key: 'news_count',
      label: t('news.categories.columns.count'),
      align: 'end',
      // Null means the server did not count, which is not the same as zero —
      // and a delete dialog that read the first as the second would tell an
      // editor a category is empty when it holds two hundred articles.
      render: (row) => (row.news_count === null ? '—' : row.news_count.toLocaleString()),
    },
    {
      key: 'actions',
      label: t('list.actions'),
      align: 'end',
      render: (row) =>
        mayManage ? (
          <span className="inline-flex gap-1">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              iconLeft={<Pencil size={14} strokeWidth={1.75} aria-hidden="true" />}
              onClick={(event) => {
                event.stopPropagation();
                setEditing(row);
              }}
            >
              {t('news.categories.edit')}
            </Button>

            <Button
              type="button"
              size="sm"
              variant="ghost"
              iconLeft={<Trash2 size={14} strokeWidth={1.75} aria-hidden="true" />}
              onClick={(event) => {
                event.stopPropagation();
                setDeleteError(null);
                setDeleting(row);
              }}
            >
              {t('common.delete')}
            </Button>
          </span>
        ) : null,
    },
  ];

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <h1 className="text-h2 font-extrabold text-primary">{t('nav.items.newsCategories')}</h1>
        <p className="mt-1 text-body-sm text-secondary">{t('news.categories.intro')}</p>
      </div>

      {isError ? <FormError message={t('errors.generic')} /> : null}
      <FormError message={deleteError} />

      <ResourceToolbar
        search={list.search}
        onSearchChange={list.setSearch}
        perPage={list.perPage}
        onPerPageChange={list.setPerPage}
        showTrashed={list.showTrashed}
        onShowTrashedChange={list.setShowTrashed}
        meta={data?.meta}
        buildPageHref={list.buildPageHref}
        primaryAction={
          mayManage ? (
            <Button
              type="button"
              size="sm"
              iconLeft={<Plus size={16} strokeWidth={1.75} aria-hidden="true" />}
              onClick={() => setEditing(null)}
            >
              {t('news.categories.add')}
            </Button>
          ) : null
        }
      />

      <ResourceTable
        rows={data?.data ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        loading={isPending}
        onOpen={mayManage ? (row) => setEditing(row) : undefined}
        emptyTitle={t('news.categories.empty.title')}
        emptyBody={t('news.categories.empty.body')}
      />

      {editing !== undefined ? (
        <CategoryDialog category={editing} onClose={() => setEditing(undefined)} />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        title={t('news.categories.delete.title')}
        body={t('news.categories.delete.body', {
          name: deleting ? valueFor(deleting.name, locale) : '',
        })}
        confirmLabel={t('common.delete')}
        // No soft delete on this table (docs/03-DATA-MODEL.md §4), so there is
        // nothing to restore afterwards and the dialog must not imply there is.
        reversible={false}
        busy={act.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) {
            return;
          }

          act.mutate(
            { id: deleting.id, action: 'delete' },
            {
              /*
               * The 409 the server sends when the category still holds
               * articles. Surfaced as a message rather than swallowed: the
               * editor's next move is to move those articles, and nothing else
               * on the screen would tell them so.
               */
              onError: (error) => setDeleteError(error.message),
              onSettled: () => setDeleting(null),
            },
          );
        }}
      />
    </div>
  );
}

interface CategoryFields {
  name: TranslatedValue;
  slug: TranslatedValue;
  description: TranslatedValue;
  color: string;
  sort_order: number;
}

function CategoryDialog({
  category,
  onClose,
}: {
  /** Null for a new one. */
  category: NewsCategoryRow | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  const [formError, setFormError] = useState<string | null>(null);

  const save = useSaveResource<NewsCategoryRow, NewsCategoryInput>('news-categories');

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CategoryFields>({
    defaultValues: {
      name: category?.name ?? {},
      slug: category?.slug ?? {},
      description: category?.description ?? {},
      color: category?.color ?? '',
      sort_order: category?.sort_order ?? 0,
    },
  });

  const name = watch('name');
  const description = watch('description');

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);

    try {
      await save.mutateAsync({
        id: category?.id,
        input: {
          name: values.name,
          slug: values.slug,
          description: values.description,
          color: values.color.trim() === '' ? null : values.color.trim(),
          sort_order: Number(values.sort_order),
        },
      });

      onClose();
    } catch (error) {
      const { formError: message, placed } = applyApiErrors(error, setError, [
        'name',
        'name.ka',
        'slug',
        'description',
        'color',
        'sort_order',
      ]);

      setFormError(message ?? (placed ? null : t('errors.generic')));
    }
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={category ? t('news.categories.edit') : t('news.categories.add')}
      closeLabel={t('common.close')}
      className="sm:w-[min(92vw,680px)]"
    >
      <form onSubmit={onSubmit} noValidate className="space-y-5">
        <FormError message={formError} />

        <TranslatableField
          label={t('news.categories.fields.name')}
          name="name"
          value={name}
          onChange={(next) => setValue('name', next, { shouldDirty: true })}
          error={errors.name?.message}
        />

        <TranslatableField
          label={t('news.categories.fields.description')}
          hint={t('news.categories.fields.descriptionHint')}
          name="description"
          multiline
          rows={2}
          value={description}
          onChange={(next) => setValue('description', next, { shouldDirty: true })}
          required={false}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            {...register('color')}
            label={t('news.categories.fields.color')}
            hint={t('news.categories.fields.colorHint')}
            placeholder="#1e3a5f"
            error={errors.color?.message}
          />

          <Input
            {...register('sort_order', { valueAsNumber: true })}
            type="number"
            min={0}
            label={t('news.categories.fields.order')}
            hint={t('news.categories.fields.orderHint')}
            error={errors.sort_order?.message}
          />
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>

          <SubmitButton loading={isSubmitting}>{t('editor.publishing.save')}</SubmitButton>
        </div>
      </form>
    </Modal>
  );
}
